import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from 'react-router'
import { prefixer } from 'stylis'
import rtlPlugin from 'stylis-plugin-rtl'

import { queryClient } from '../api/queryClient'
import { PortalProvider } from '../auth/PortalProvider'
import { ErrorBoundary } from '../components/ErrorBoundary'
import '../styles/fonts.css'
import { theme } from '../theme/theme'
import { router } from './router'

/**
 * Nothing in the portal is reachable without a session any more.
 *
 * There used to be three such pages — activation, password reset, and asking
 * for one — because somebody setting a password had no way to sign in and ask.
 * Nobody sets a password now: the login screen itself is the whole
 * unauthenticated surface, and the session gate renders it.
 */

/**
 * Emotion's cache, taught to mirror what it emits.
 *
 * The theme's `direction` tells MUI which way round its components are. It
 * does not rewrite the CSS they generate, so paddings and margins declared as
 * left/right stay put and the portal comes out half mirrored.
 */
const rtlCache = createCache({
  key: 'mui-rtl',
  stylisPlugins: [prefixer, rtlPlugin],
})

export function App() {
  return (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            {/* Everything below is behind a live session and a chosen customer.
                PortalProvider renders the sign-in screen when there is none. */}
            <PortalProvider>
              <RouterProvider router={router} />
            </PortalProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </CacheProvider>
  )
}
